import { Button, Menu, MenuItem, MenuSub } from '@affine/component';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { DocsService } from '@affine/core/modules/doc';
import { EditorSettingService } from '@affine/core/modules/editor-setting';
import { TemplateDocService } from '@affine/core/modules/template-doc';
import { TemplateListMenuContentScrollable } from '@affine/core/modules/template-doc/view/template-list-menu';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { inferOpenMode } from '@affine/core/utils';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import type { DocMode } from '@blocksuite/affine/model';
import {
  ArrowDownSmallIcon,
  EdgelessIcon,
  ImportIcon,
  PageIcon,
  PlusIcon,
  TemplateIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import type React from 'react';
import { type MouseEvent, useCallback } from 'react';

import * as styles from './index.css';

/**
 * @return a function to create a new doc, will duplicate the template doc if the page template is enabled
 */
const useNewDoc = () => {
  const workspaceService = useService(WorkspaceService);
  const templateDocService = useService(TemplateDocService);
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;

  const currentWorkspace = workspaceService.workspace;
  const enablePageTemplate = useLiveData(
    templateDocService.setting.enablePageTemplate$
  );
  const pageTemplateDocId = useLiveData(
    templateDocService.setting.pageTemplateDocId$
  );

  const pageHelper = usePageHelper(currentWorkspace.docCollection);

  const createPage = useAsyncCallback(
    async (e?: MouseEvent, mode?: DocMode) => {
      if (enablePageTemplate && pageTemplateDocId) {
        const docId =
          await docsService.duplicateFromTemplate(pageTemplateDocId);
        workbench.openDoc(docId, { at: inferOpenMode(e) });
      } else {
        pageHelper.createPage(mode, { at: inferOpenMode(e) });
      }
    },
    [docsService, enablePageTemplate, pageHelper, pageTemplateDocId, workbench]
  );

  return createPage;
};

interface AddPageButtonProps {
  className?: string;
  style?: React.CSSProperties;
  /**
   * Opens the import dialog. Omitted = no Import entry in the menu.
   */
  importFile?: () => void;
}

const sideBottom = { side: 'bottom' as const };
export function AddPageButton(props: AddPageButtonProps) {
  const editorSetting = useService(EditorSettingService);
  const newDocDefaultMode = useLiveData(
    editorSetting.editorSetting.settings$.selector(s => s.newDocDefaultMode)
  );

  return newDocDefaultMode === 'ask' ? (
    <AddPageWithAsk {...props} />
  ) : (
    <AddPageWithoutAsk {...props} />
  );
}

/**
 * The menu behind the sidebar (+), mirroring the top-bar "New doc" dropdown.
 *
 * YOD-364: Import belongs here because it must be reachable WITHOUT creating a
 * doc first. Previously the only route from the sidebar was to hit (+), get an
 * untitled doc, then find Import in that doc's ... menu — which left the
 * untitled doc behind. That was never an error, just the wrong entry point.
 */
function NewDocMenuItems({ importFile }: { importFile?: () => void }) {
  const t = useI18n();
  const createDoc = useNewDoc();
  const workbench = useService(WorkbenchService).workbench;
  const docsService = useService(DocsService);

  const createPage = useCallback(
    (e?: MouseEvent) => {
      createDoc(e, 'page');
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'page' });
    },
    [createDoc]
  );
  const createEdgeless = useCallback(
    (e?: MouseEvent) => {
      createDoc(e, 'edgeless');
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'edgeless' });
    },
    [createDoc]
  );

  const createDocFromTemplate = useAsyncCallback(
    async (templateId: string) => {
      const docId = await docsService.duplicateFromTemplate(templateId);
      workbench.openDoc(docId);
      track.$.sidebar.newDoc.quickStart({ with: 'template' });
    },
    [docsService, workbench]
  );

  return (
    <>
      <MenuItem
        prefixIcon={<PageIcon />}
        onClick={createPage}
        onAuxClick={createPage}
      >
        {t['Page']()}
      </MenuItem>
      <MenuItem
        prefixIcon={<EdgelessIcon />}
        onClick={createEdgeless}
        onAuxClick={createEdgeless}
      >
        {t['Edgeless']()}
      </MenuItem>
      <MenuSub
        triggerOptions={{
          prefixIcon: <TemplateIcon />,
        }}
        subContentOptions={{
          sideOffset: 16,
          className: styles.templateMenu,
        }}
        items={
          <TemplateListMenuContentScrollable onSelect={createDocFromTemplate} />
        }
      >
        {t['Template']()}
      </MenuSub>
      {importFile ? (
        <MenuItem
          prefixIcon={<ImportIcon />}
          onClick={importFile}
          data-testid="sidebar-new-page-import"
        >
          {t['com.affine.new_import']()}
        </MenuItem>
      ) : null}
    </>
  );
}

function AddPageWithAsk({ className, style, importFile }: AddPageButtonProps) {
  const t = useI18n();

  return (
    <Menu items={<NewDocMenuItems importFile={importFile} />}>
      <Button
        tooltip={t['New Page']()}
        tooltipOptions={sideBottom}
        data-testid="sidebar-new-page-with-ask-button"
        className={clsx([styles.withAskRoot, className])}
        style={style}
      >
        <div className={styles.withAskContent}>
          <PlusIcon />
          <ArrowDownSmallIcon />
        </div>
      </Button>
    </Menu>
  );
}

function AddPageWithoutAsk({
  className,
  style,
  importFile,
}: AddPageButtonProps) {
  const createDoc = useNewDoc();

  const onClickNewPage = useCallback(
    (e?: MouseEvent) => {
      // Creating is this half's job, not the menu's.
      e?.stopPropagation();
      createDoc(e);
      track.$.navigationPanel.$.createDoc();
    },
    [createDoc]
  );

  // The menu trigger opens on pointerdown, ahead of click, so the create half
  // has to swallow that too or the menu flashes open on every new doc.
  const keepMenuClosed = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  const t = useI18n();

  return (
    <Menu items={<NewDocMenuItems importFile={importFile} />}>
      <Button
        tooltip={t['New Page']()}
        tooltipOptions={sideBottom}
        data-testid="sidebar-new-page-split-button"
        className={clsx([styles.withAskRoot, className])}
        style={style}
      >
        <div className={styles.withAskContent}>
          {/* Keeps the original testid: a click here still creates a doc
              immediately, so the existing e2e suite is unaffected. */}
          <span
            className={styles.createZone}
            data-testid="sidebar-new-page-button"
            onPointerDown={keepMenuClosed}
            onClick={onClickNewPage}
            onAuxClick={onClickNewPage}
          >
            <PlusIcon />
          </span>
          <span className={styles.splitDivider} />
          <ArrowDownSmallIcon />
        </div>
      </Button>
    </Menu>
  );
}
